// =====================================================================
// rdvDocs.js — Commandes prêtes v3 B (16-10-2026) : rendez-vous + documents à imprimer — fonctions PURES
// =====================================================================
// Pages publiques (/order/<jeton>/rdv, /order/<jeton>/documents, anglais puis français) et écrans Zeus
// (pastille, documents à imprimer). Mêmes règles que le serveur (backend/src/services/ready-rdv-docs.rules.js,
// comparé par frontend/test/commande-prete.test.mjs) : l'écran ne propose jamais ce que le serveur refuse.
// =====================================================================

const deux = (en, fr) => `${en} / ${fr}`;

// ---------- Rendez-vous ----------
export const DEMI_JOURNEES = Object.freeze({
  AM: Object.freeze({ en: 'Morning (8:00–12:00)', fr: 'Matin (8h00–12h00)', motEn: 'morning', motFr: 'matin' }),
  PM: Object.freeze({ en: 'Afternoon (12:00–16:00)', fr: 'Après-midi (12h00–16h00)', motEn: 'afternoon', motFr: 'après-midi' }),
});
/** Demi-journée connue (AM / PM) — clés propres seulement (comme le serveur). */
export const demiJournee = (code) => (typeof code === 'string' && Object.hasOwn(DEMI_JOURNEES, code) ? DEMI_JOURNEES[code] : null);
const JOUR_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const midi = (j) => {
  const m = typeof j === 'string' ? JOUR_RE.exec(j) : null;
  if (!m) return null;
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12);
  return new Date(t).toISOString().slice(0, 10) === j ? new Date(t) : null;
};
/** « Mon 19/10 » / « lun. 19/10 ». */
export function jourCourt(j, langue = 'fr') {
  const d = midi(j);
  if (!d) return String(j ?? '');
  const jour = d.toLocaleDateString(langue === 'en' ? 'en-GB' : 'fr-BE', { weekday: 'short', timeZone: 'UTC' });
  return `${jour} ${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}
/** « Monday 19 October 2026 » / « lundi 19 octobre 2026 ». */
export function jourLong(j, langue = 'fr') {
  const d = midi(j);
  if (!d) return String(j ?? '');
  return d.toLocaleDateString(langue === 'en' ? 'en-GB' : 'fr-BE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).replace(',', '');
}
/** « lundi 19 octobre 2026, matin (8h00–12h00) ». */
export function texteRdv(r, langue = 'fr') {
  const dj = demiJournee(r?.demiJournee);
  return `${jourLong(r?.jour, langue)}${dj ? `, ${(langue === 'en' ? dj.en : dj.fr).toLowerCase()}` : ''}`;
}

/**
 * Rien ne part à l'adresse saisie : le message EXACT (jamais « un mail part » quand il ne part pas, ni
 * « les contacts sont informés » : plafond, commande de test, client sans contact… ils ne le sont pas toujours).
 */
export const MSG_SANS_CONFIRMATION = Object.freeze({
  en: 'Your booking is registered. No confirmation email is sent to this address.',
  fr: 'Votre rendez-vous est enregistré. Aucune confirmation n\'est envoyée à cette adresse.',
});
/**
 * Message après « Confirmer » (réponse du serveur) → { texte (EN), texteFr }. confirmationA : adresse qui
 * reçoit VRAIMENT la confirmation ; sinon (null : adresse qui n'est pas un contact du client, plafond,
 * commande de test, hors production…) MSG_SANS_CONFIRMATION — sauf un renvoi identique d'une adresse connue
 * (« déjà réservé » : rien de nouveau, rien à dire de plus).
 */
export function texteRdvPris(r) {
  const en = [`✓ ${r && r.inchange ? 'Already booked' : 'Booked'}: ${texteRdv(r && r.rdv, 'en')}.`];
  const fr = [`✓ ${r && r.inchange ? 'Déjà réservé' : 'Rendez-vous confirmé'} : ${texteRdv(r && r.rdv, 'fr')}.`];
  if (r && r.confirmationA) {
    en.push(`A confirmation email is on its way to ${r.confirmationA}.`);
    fr.push(`Une confirmation part à ${r.confirmationA}.`);
  } else if (r && (!r.inchange || r.adresseNonConfirmee)) {
    en.push(MSG_SANS_CONFIRMATION.en);
    fr.push(MSG_SANS_CONFIRMATION.fr);
  }
  return { texte: en.join(' '), texteFr: fr.join(' ') };
}

// Même règle d'adresse que le serveur (ready-order.rules.js, emailValide).
const EMAIL = /^[^@\s,;<>"()]+@[^@\s,;<>"()]+\.[^@\s,;<>"()]+$/;
export const emailValide = (s) => typeof s === 'string' && s.length <= 254 && EMAIL.test(s);
const libre = (v) => (typeof v === 'string' ? v.replace(/\s+/g, ' ').trim() : '');

/** Ce qui manque au formulaire du rendez-vous (vide = prêt) — mêmes règles que lireRdv du serveur. */
export function verifRdv(f, jours = []) {
  const pb = [];
  if (!f?.jour || !demiJournee(f?.demiJournee)) pb.push(deux('a day and morning or afternoon', 'un jour et matin ou après-midi'));
  else if (!jours.includes(f.jour)) pb.push(deux('this day can no longer be booked, choose another one', 'ce jour n\'est plus proposé, choisissez-en un autre'));
  const societe = libre(f?.societe); const nom = libre(f?.nom); const email = libre(f?.email); const tel = libre(f?.tel); const plaque = libre(f?.plaque).toUpperCase();
  if (!societe) pb.push(deux('company', 'société')); else if (societe.length > 100) pb.push(deux('company: 100 characters at most', 'société : 100 caractères au plus'));
  if (!nom) pb.push(deux('contact name', 'nom')); else if (nom.length > 100) pb.push(deux('contact name: 100 characters at most', 'nom : 100 caractères au plus'));
  if (!emailValide(email)) pb.push(deux('a valid email', 'un e-mail valide'));
  if (tel && !/^\+?[0-9][0-9 ()./-]{4,28}$/.test(tel)) pb.push(deux('phone: digits only (e.g. +32 64 00 00 00)', 'téléphone : chiffres seulement'));
  if (plaque && !/^[A-Z0-9][A-Z0-9 .-]{0,19}$/.test(plaque)) pb.push(deux('truck plate: letters and digits, 20 at most', 'plaque : lettres et chiffres, 20 au plus'));
  return pb;
}
export const texteManque = (pb) => `${deux('Please check', 'Vérifiez')} : ${pb.join(' · ')}`;
/** Corps envoyé au serveur (espaces regroupés ; plaque en majuscules ; champs vides retirés). */
export function corpsRdv(f) {
  const c = { jour: f.jour, demiJournee: f.demiJournee, societe: libre(f.societe), nom: libre(f.nom), email: libre(f.email) };
  if (libre(f.tel)) c.tel = libre(f.tel);
  if (libre(f.plaque)) c.plaque = libre(f.plaque).toUpperCase();
  return c;
}

// ---------- Documents ----------
export const TYPES_DOCUMENT = Object.freeze([
  Object.freeze({ code: 'ETIQUETTE', en: 'Carrier label', fr: 'Étiquette transporteur' }),
  Object.freeze({ code: 'CMR', en: 'CMR / consignment note', fr: 'CMR / lettre de voiture' }),
  Object.freeze({ code: 'PACKING', en: 'Packing list', fr: 'Packing list' }),
  Object.freeze({ code: 'FACTURE', en: 'Invoice', fr: 'Facture' }),
  Object.freeze({ code: 'AUTRE', en: 'Other', fr: 'Autre' }),
]);
export const libelleType = (code, langue = 'fr') => TYPES_DOCUMENT.find((t) => t.code === code)?.[langue] || code || '—';
export const MAX_FICHIERS = 10;
export const MAX_OCTETS = 15 * 1024 * 1024;
export const MAX_COPIES = 99;
export const ACCEPT = '.pdf,.zpl,.prn,.txt,.png,.jpg,.jpeg';
const EXT_PERMISES = /\.(pdf|zpl|prn|txt|png|jpe?g)$/i;

/** Type deviné d'après le nom (à confirmer par l'utilisateur) ; '' = à choisir. ZPL = étiquette. */
export function devinerType(nom) {
  const s = String(nom ?? '').toLowerCase();
  if (/\.(zpl|prn)$/.test(s) || /label|etiq|étiq/.test(s)) return 'ETIQUETTE';
  if (/\bcmr\b|lettre.de.voiture|consignment/.test(s)) return 'CMR';
  if (/packing|colisage/.test(s)) return 'PACKING';
  if (/invoice|facture|factuur/.test(s)) return 'FACTURE';
  return '';
}
export const tailleTexte = (n) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
/** Contrôle AVANT l'envoi d'un fichier choisi (le serveur revérifie, par signature) : null = accepté. */
export function verifFichierLocal(f, nDeja = 0) {
  if (nDeja >= MAX_FICHIERS) return deux(`${MAX_FICHIERS} files at most`, `${MAX_FICHIERS} fichiers au plus`);
  if (!EXT_PERMISES.test(String(f?.name ?? ''))) return deux('Only PDF, ZPL, PNG or JPG files are accepted', 'Seuls les fichiers PDF, ZPL, PNG ou JPG sont acceptés');
  if (!f?.size) return deux('Empty file', 'Fichier vide');
  if (f.size > MAX_OCTETS) return deux('File too large: 15 MB at most', 'Fichier trop lourd : 15 Mo au plus');
  return null;
}
/** Copies : entier 1–99, sinon null (même règle que le serveur). */
export function lireCopies(v) {
  const s = typeof v === 'number' ? String(v) : (typeof v === 'string' ? v.trim() : '');
  if (!/^\d{1,2}$/.test(s)) return null;
  const n = Number(s);
  return n >= 1 && n <= MAX_COPIES ? n : null;
}
/** Type enregistré (ZPL : toujours étiquette — une Zebra seule l'imprime). */
export const typeEffectif = (f) => (f?.format === 'ZPL' ? 'ETIQUETTE' : f?.type);

/** Le dépôt est-il fermé ce jour-là (week-end, jour férié de la liste du serveur) ? (même règle que le serveur) */
export const jourFerme = (j, feries = []) => { const d = midi(j); return !d || [0, 6].includes(d.getUTCDay()) || feries.includes(j); };
export const MSG_JOUR_FERME = deux('pick-up date: our depot is closed that day (weekend or public holiday)', 'date d\'enlèvement : dépôt fermé ce jour-là (week-end ou jour férié)');

/**
 * Ce qui empêche « Envoyer » (vide = prêt). fichiers : [{ etat: 'envoi'|'pret'|'refuse', nom, type, copies, format }].
 * Les fichiers refusés ne partent pas (ils restent affichés avec leur motif, ✕ pour les retirer).
 */
export function verifEnvoi({ transporteur, dateEnlevement, aujourdhui, feries = [], fichiers = [] }) {
  const pb = [];
  const t = libre(transporteur);
  if (!t) pb.push(deux('carrier', 'transporteur')); else if (t.length > 100) pb.push(deux('carrier: 100 characters at most', 'transporteur : 100 caractères au plus'));
  if (dateEnlevement) {
    if (!midi(dateEnlevement)) pb.push(deux('pick-up date: invalid', 'date d\'enlèvement invalide'));
    else if (aujourdhui && dateEnlevement < aujourdhui) pb.push(deux('pick-up date: already past', 'date d\'enlèvement déjà passée'));
    else if (aujourdhui && dateEnlevement > dateMax(aujourdhui)) pb.push(deux('pick-up date: within one year', 'date d\'enlèvement : dans l\'année'));
    else if (jourFerme(dateEnlevement, feries)) pb.push(MSG_JOUR_FERME);
  }
  if (fichiers.some((f) => f.etat === 'envoi')) pb.push(deux('wait for the files to finish uploading', 'attendez la fin de l\'envoi des fichiers'));
  const prets = fichiers.filter((f) => f.etat === 'pret');
  if (!prets.length) pb.push(deux('at least one file', 'au moins un fichier'));
  if (prets.length > MAX_FICHIERS) pb.push(deux(`${MAX_FICHIERS} files at most`, `${MAX_FICHIERS} fichiers au plus`));
  prets.forEach((f) => {
    if (!typeEffectif(f)) pb.push(deux(`what is « ${f.nom} »?`, `qu'est-ce que « ${f.nom} » ?`));
    if (lireCopies(f.copies) === null) pb.push(deux(`copies 1–${MAX_COPIES} for « ${f.nom} »`, `copies 1–${MAX_COPIES} pour « ${f.nom} »`));
  });
  return pb;
}
/** Corps de « Envoyer » : les fichiers prêts seulement. */
export function corpsEnvoi({ transporteur, dateEnlevement, instructions, fichiers, lot }) {
  const c = {
    ...(lot ? { lot } : {}),   // clé d'envoi : un renvoi après une réponse perdue ne crée jamais un 2e lot
    transporteur: libre(transporteur),
    fichiers: fichiers.filter((f) => f.etat === 'pret').map((f) => ({ id: f.id, type: typeEffectif(f), copies: lireCopies(f.copies) })),
  };
  if (dateEnlevement) c.dateEnlevement = dateEnlevement;
  if (String(instructions ?? '').trim()) c.instructions = String(instructions).trim();
  return c;
}
/** Date d'enlèvement la plus lointaine proposée (aujourd'hui + 365 jours, comme le serveur). */
export function dateMax(aujourdhui) {
  const d = midi(aujourdhui);
  return d ? new Date(d.getTime() + 365 * 86_400_000).toISOString().slice(0, 10) : undefined;
}

// ---------- Zeus : pastille, impression ----------
// 21/10 (décision Silviu) : « à traiter » montre UNE ligne par commande — la pastille compte les COMMANDES à
// imprimer (`commandesAImprimer`), plus les documents ; un serveur qui ne la donne pas : les documents, comme avant.
export const pastille = (c) => (c ? (Number(c.rdvAVoir) || 0) + (Number(c.commandesAImprimer ?? c.documentsAImprimer) || 0) : 0);
export function texteCompteurs(c) {
  const r = Number(c?.rdvAVoir) || 0; const d = Number(c?.documentsAImprimer) || 0;
  const t = [];
  if (r) t.push(`${r} rendez-vous à voir`);
  if (d) t.push(`${d} document${d > 1 ? 's' : ''} à imprimer`);
  return t.join(' · ') || 'Rien à traiter';
}
export const ETATS_MAIL = Object.freeze({
  ENVOYE: 'mail envoyé', ECHEC: 'mail NON parti', EN_COURS: 'mail en cours (nouvel essai)…', NON_CONFIGURE: 'mail non envoyé (envoi des mails non configuré sur ce serveur)', AUCUN: 'pas de mail (aucun destinataire réglé)',
});
export const texteEtatMail = (s) => (s ? ETATS_MAIL[s] || s : 'mail en cours…');
/** État de la confirmation d'un rendez-vous, selon à qui elle est RÉELLEMENT partie (serveur, confirmState). */
export const ETATS_CONFIRMATION = Object.freeze({
  ENVOYE: 'envoyée au demandeur (copie aux contacts du client)',
  ENVOYE_CONTACTS: 'envoyée aux contacts du client seulement (adresse du demandeur inconnue du client)',
  ENVOYE_TEST: 'envoyée à l\'adresse de test (commande de test / hors production)',
});
export const texteEtatConfirmation = (s) => ETATS_CONFIRMATION[s] || texteEtatMail(s);

export const PONT_IMPRESSION = 'http://127.0.0.1:9110';
// Le ZPL voyage en OCTETS, jamais décodé puis réencodé : une étiquette en latin1 (^CI0, « Liège »)
// arrive à la Zebra telle que le transporteur l'a faite.
const enOctets = (z) => (typeof z === 'string' ? new TextEncoder().encode(z) : (z instanceof Uint8Array ? z : new Uint8Array(z)));
/**
 * Envoie le ZPL (texte ou octets, tel quel) au pont d'impression local (Imprimante-Bridge.ps1,
 * POST /print) : UNE requête par copie, le fichier une seule fois dans chacune (jamais un corps de
 * 15 Mo × 99). Renvoie le nombre de copies envoyées. Lève une erreur `code` + `envoyees` (copies déjà
 * parties) : PONT_ABSENT (rien n'écoute, ou accès local refusé par le navigateur), PONT_ERREUR (le pont a
 * répondu une erreur : cette copie n'est pas imprimée), PONT_DELAI (pas de réponse en `delaiMs` : cette
 * copie est PEUT-ÊTRE partie). 30 s : de quoi répondre à la demande d'accès au réseau local du navigateur.
 */
export async function envoyerAuPont(zpl, copies, { fetchFn = globalThis.fetch, delaiMs } = {}) {
  const corps = enOctets(zpl);
  // 30 s + 10 s par Mo : le pont lit le corps octet par octet (un gros ZPL avec graphiques est lent).
  const delai = delaiMs ?? 30_000 + Math.ceil(corps.length / 1_000_000) * 10_000;
  const n = Math.max(1, lireCopies(copies) || 1);
  for (let i = 0; i < n; i++) {
    const ctrl = new AbortController();
    let depasse = false;
    const minuteur = setTimeout(() => { depasse = true; ctrl.abort(); }, delai);
    let r;
    try {
      r = await fetchFn(`${PONT_IMPRESSION}/print`, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: corps, signal: ctrl.signal });
    } catch (_e) {
      throw Object.assign(new Error(depasse ? 'Pont d\'impression : pas de réponse' : 'Pont d\'impression absent'), { code: depasse ? 'PONT_DELAI' : 'PONT_ABSENT', envoyees: i });
    } finally { clearTimeout(minuteur); }
    if (!r.ok) throw Object.assign(new Error(`Pont d'impression : HTTP ${r.status}`), { code: 'PONT_ERREUR', envoyees: i });
  }
  return n;
}
export const MSG_PONT_ABSENT = 'Pont d\'impression local non détecté : le fichier ZPL est téléchargé. Lancez « Demarrer-impression.bat » sur ce poste puis réessayez « Imprimer ».';
export const MSG_PONT_DELAI = 'Le pont d\'impression ne répond pas : l\'impression est peut-être en cours. Vérifiez la Zebra AVANT de réessayer (sinon étiquettes en double).';
/**
 * Message après un échec ; `telecharger` : faut-il télécharger le fichier ? Seulement si RIEN n'est parti
 * (jamais si une copie est peut-être imprimée, ni si des copies le sont déjà : risque de doublon).
 */
export function apresEchecPont(e) {
  const deja = Number(e?.envoyees) || 0;
  const partiel = deja ? ` ${deja} copie${deja > 1 ? 's' : ''} déjà envoyée${deja > 1 ? 's' : ''} : n'imprimez que le reste.` : '';
  if (e?.code === 'PONT_DELAI') return { telecharger: false, texte: `${MSG_PONT_DELAI}${partiel}` };
  if (deja) return { telecharger: false, texte: `${e?.message || 'Pont d\'impression interrompu'} (Zebra injoignable ?).${partiel}` };
  if (e?.code === 'PONT_ERREUR') return { telecharger: true, texte: `${e.message} (Zebra injoignable ?) : rien n'est imprimé, le fichier ZPL est téléchargé.` };
  return { telecharger: true, texte: MSG_PONT_ABSENT };
}

// ---------- v3 C (17-10-2026) « 🖨 Imprimer tout » (SPEC v3 C §1) : état affiché, bouton ----------
// Le serveur donne, par document, `impression` = dernier travail { statut EN_FILE|EN_COURS|FAIT|ECHEC, cible,
// erreur } (ou null), et `impressionAuto` = { disponible, message } (agent de server-home vu depuis < 60 s).
export const LIBELLES_CIBLE = Object.freeze({ ZEBRA: 'Zebra quai (20.18)', KONICA: 'Konica 2100' });
export const libelleCible = (c) => LIBELLES_CIBLE[c] || c || '—';
export const MSG_AUTO_INCONNUE = 'Impression automatique : état inconnu — utilisez « Ouvrir »';
/** Impression automatique possible maintenant ? → { disponible, raison } (raison : à afficher AVANT le clic). */
export function dispoImpression(auto) {
  if (!auto || typeof auto !== 'object') return { disponible: false, raison: MSG_AUTO_INCONNUE };
  return auto.disponible === true ? { disponible: true, raison: null } : { disponible: false, raison: auto.message || MSG_AUTO_INCONNUE };
}
export const travailActif = (d) => ['EN_FILE', 'EN_COURS'].includes(d?.impression?.statut);
/**
 * Ce que « Imprimer tout » enverrait : reçus, pas encore imprimés, pas déjà en file — ni en ÉCHEC (peut-être sorti :
 * « ↻ Réessayer » par document, après vérification ; même règle que le serveur).
 */
export const aImprimerTout = (docs) => (Array.isArray(docs) ? docs : []).filter((d) => d?.statut === 'RECU' && !travailActif(d) && d?.impression?.statut !== 'ECHEC');
/** Documents de « Imprimer tout », regroupés par commande (la route est par commande) → [[orderId, [ids]], …]. */
export function lotsImprimerTout(docs) {
  const m = new Map();
  for (const d of aImprimerTout(docs)) { if (!m.has(d.orderId)) m.set(d.orderId, []); m.get(d.orderId).push(d.id); }
  return [...m.entries()];
}
/**
 * Bouton « 🖨 Imprimer tout (N) » → null (rien à imprimer, rien en file : pas de bouton) ou { texte, desactive, note }.
 * Pendant l'envoi ou tant qu'il reste des travaux : « En cours… » (un 2e clic ne réimprime rien) ; agent
 * indisponible : désactivé, et la note le DIT avant le clic.
 */
export function boutonImprimerTout({ documents, auto, enCours = false } = {}) {
  const n = aImprimerTout(documents).length;
  const actifs = (Array.isArray(documents) ? documents : []).filter(travailActif).length;
  if (!n && !actifs && !enCours) return null;
  const { disponible, raison } = dispoImpression(auto);
  if (enCours || actifs) return { texte: 'En cours…', desactive: true, note: raison || (actifs ? `${actifs} document${actifs > 1 ? 's' : ''} en file d'impression` : null) };
  return { texte: `🖨 Imprimer tout (${n})`, desactive: !disponible, note: raison };
}
/** Message après « Imprimer tout » (n = travaux créés). */
export const texteLancement = (n) => (n > 0
  ? `${n} document${n > 1 ? 's' : ''} envoyé${n > 1 ? 's' : ''} à l'impression (chacun sur son imprimante).`
  : 'Rien de nouveau à imprimer (déjà imprimé ou déjà en file).');
/**
 * État d'impression d'un document → { texte, classe: 'attente'|'err'|null, action: 'reessayer'|'reimprimer'|null }.
 * `disponible` : l'agent est-il là (sinon un travail en file attend son retour — dit clairement).
 */
export function etatImpressionDoc(d, disponible = true) {
  const t = d?.impression;
  if (t?.statut === 'EN_FILE') return { texte: disponible ? 'En file…' : 'En file… — agent d\'impression hors ligne : partira à son retour', classe: 'attente', action: null };
  if (t?.statut === 'EN_COURS') return { texte: 'Impression en cours…', classe: 'attente', action: null };
  if (t?.statut === 'ECHEC') return { texte: `Échec : ${t.erreur || 'raison inconnue'}`, classe: 'err', action: 'reessayer' };
  return { texte: null, classe: null, action: d?.statut === 'IMPRIME' ? 'reimprimer' : null };
}
/** Document imprimé : « Imprimé ✓ — Zebra quai (20.18) » (par l'agent) ou « Imprimé ✓ » (marqué à la main). */
export const texteImprime = (d) => `Imprimé ✓${d?.impression?.statut === 'FAIT' ? ` — ${libelleCible(d.impression.cible)}` : ''}`;

// ---------- 21-10-2026 (décision Silviu) « À traiter » : UNE ligne par commande, UN bouton « 🖨 Imprimer » ----------
// La liste dit seulement combien de documents partent sur quelle imprimante ; leur détail (Ouvrir, ↻ Réessayer,
// Marquer imprimé) reste sur l'écran de la commande.
/** En-tête : « 1 rendez-vous à voir · 2 commandes avec documents à imprimer ». */
export function texteAFaire(rdv, commandes) {
  const r = Number(rdv) || 0; const c = Number(commandes) || 0;
  const t = [];
  if (r) t.push(`${r} rendez-vous à voir`);
  if (c) t.push(`${c} commande${c > 1 ? 's' : ''} avec documents à imprimer`);
  return t.join(' · ') || 'Rien à traiter';
}
/**
 * Documents « à traiter » regroupés par commande, dans l'ordre d'arrivée → [{ orderId, commande, docs, recuLe (le
 * dernier reçu), enlevement (la date souhaitée la plus proche), instructions, aImprimer: [ids] (ce que « Imprimer »
 * enverrait), actifs (en file / en cours), echecs }].
 */
export function lignesParCommande(docs) {
  const m = new Map();
  for (const d of Array.isArray(docs) ? docs : []) {
    if (!d || !d.orderId) continue;
    if (!m.has(d.orderId)) m.set(d.orderId, { orderId: d.orderId, commande: d.commande || null, docs: [] });
    m.get(d.orderId).docs.push(d);
  }
  return [...m.values()].map((l) => {
    const recus = l.docs.map((d) => d.recuLe).filter(Boolean).map(String).sort();
    const enlevements = l.docs.map((d) => d.dateEnlevement).filter((j) => typeof j === 'string' && j).sort();
    const instructions = [...new Set(l.docs.map((d) => (typeof d.instructions === 'string' ? d.instructions.trim() : '')).filter(Boolean))];
    return {
      ...l,
      recuLe: recus.length ? recus[recus.length - 1] : null,
      enlevement: enlevements[0] || null,
      instructions: instructions.length ? instructions.join(' · ') : null,
      aImprimer: aImprimerTout(l.docs).map((d) => d.id),
      actifs: l.docs.filter(travailActif).length,
      echecs: l.docs.filter((d) => d.impression?.statut === 'ECHEC'),
    };
  });
}
/** « 4 documents : 3 → Konica 2100 · 1 → Zebra quai (20.18) » ; plus d'une copie : « (5 exemplaires) ». */
export function texteResumeCommande(docs) {
  const liste = Array.isArray(docs) ? docs : [];
  const par = new Map();
  for (const d of liste) {
    const x = par.get(d?.cible || null) || { n: 0, ex: 0 };
    x.n += 1; x.ex += lireCopies(d?.copies) || 1;
    par.set(d?.cible || null, x);
  }
  const n = liste.length;
  const parts = [...par.entries()].map(([c, x]) => `${x.n} → ${libelleCible(c)}${x.ex > x.n ? ` (${x.ex} exemplaires)` : ''}`);
  return `${n} document${n > 1 ? 's' : ''}${parts.length ? ` : ${parts.join(' · ')}` : ''}`;
}
/**
 * État d'une ligne → { texte, classe } ou null. `envoi` = { n } noté au clic : « Impression en cours… 2/4 » ; sans lui
 * (page rechargée pendant l'impression) : le nombre encore en file. Un document imprimé quitte la liste.
 */
export function etatLigneCommande(l, { disponible = true, envoi = null } = {}) {
  if (!l) return null;
  if (l.actifs) {
    if (!disponible) return { texte: `En file (${l.actifs}) — agent d'impression hors ligne : partira à son retour`, classe: 'attente' };
    const n = Number(envoi?.n) || 0;
    return { texte: n ? `Impression en cours… ${Math.min(n, Math.max(0, n - l.actifs))}/${n}` : `Impression en cours… (${l.actifs} en file)`, classe: 'attente' };
  }
  if (l.echecs?.length) {
    const m = l.echecs.length;
    return { texte: `Échec sur ${m} document${m > 1 ? 's' : ''} : ${l.echecs[0].impression?.erreur || 'raison inconnue'} — vérifiez l'imprimante, puis « ↻ Réessayer » dans la commande`, classe: 'err' };
  }
  return null;
}
/**
 * Bouton « 🖨 Imprimer » d'une ligne → null (rien à envoyer, rien en file) ou { texte, desactive, note } ; agent
 * indisponible : désactivé, et la note le DIT avant le clic (sans « utilisez « Ouvrir » » : pas de bouton Ouvrir ici).
 */
export function boutonLigneCommande(l, { auto, enCours = false } = {}) {
  const n = l?.aImprimer?.length || 0;
  if (!n && !l?.actifs && !enCours) return null;
  if (enCours || l?.actifs) return { texte: 'En cours…', desactive: true, note: null };
  const { disponible, raison } = dispoImpression(auto);
  return { texte: '🖨 Imprimer', desactive: !disponible, note: disponible ? null : String(raison).replace(/\s*—\s*utilisez « Ouvrir »\s*$/, '') };
}
/** Ligne partie de la liste après « Imprimer » : relue sur la commande → { texte, ok } (jamais « ✓ » sans la relecture). */
export function texteFinCommande(ref, ids, documents) {
  const envoyes = Array.isArray(ids) ? ids : [];
  const n = envoyes.length;
  const imprimes = (Array.isArray(documents) ? documents : []).filter((d) => envoyes.includes(d?.id) && d.statut === 'IMPRIME');
  const ok = imprimes.length;
  // « chacun sur son imprimante » seulement si l'agent les a TOUS imprimés (pas « Marquer imprimé » à la main entre-temps)
  const parAgent = imprimes.every((d) => d.impression?.statut === 'FAIT');
  if (n && ok === n) return { texte: `${ref} : ${n} document${n > 1 ? 's' : ''} imprimé${n > 1 ? 's' : ''} ✓${parAgent ? ' — chacun sur son imprimante' : ''}`, ok: true };
  return { texte: `${ref} : ${ok}/${n} imprimé${ok > 1 ? 's' : ''} — voyez la commande`, ok: false };
}
